import { cloneElement, useCallback, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import { CheckIcon, XIcon } from "lucide-react";

import { hasPermission } from "simplydo/core/permissions";
import { initUseQuery } from "simplydo/core/query";
import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Dropdown } from "simplydo/react/ui/dropdown";
import { H4, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import {
  LabelDetail,
  Label as SLabel,
  type LabelSize,
} from "simplydo/react/ui/label";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import { Popup } from "simplydo/react/ui/popup";

import api from "api";
import { useAppSelector } from "store";

type LabelStackViewProps = {
  labels: string[];
  horizontal?: boolean;
  size?: LabelSize;
};

export const LabelStackView = ({
  labels,
  horizontal,
  size = "small",
}: LabelStackViewProps) => {
  if (!labels || labels.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        flexDirection: horizontal ? "row" : "column",
        gap: horizontal ? 2 : 4,
      }}
    >
      {labels?.map((label) => (
        <div key={label}>
          <LabelItem label={label} size={size} />
        </div>
      ))}
    </div>
  );
};

type LabelItemProps = {
  label: string;
  onRemove?: (label: string) => void;
  size?: LabelSize;
  style?: React.CSSProperties;
};

const StyledLabelContent = styled.div`
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
`;

export const LabelItem = ({
  label,
  onRemove,
  size = "small",
  style,
}: LabelItemProps) => (
  // Default semantic padding makes the label too big for our use case
  // Default semantic color sets the label to .6 alpha which works for text but makes emojis look faded
  <SLabel
    size={size}
    style={{ padding: "0.2em 0.6em", color: "#5d5d5d", ...style }}
  >
    <StyledLabelContent>{label}</StyledLabelContent>
    {onRemove && (
      <LabelDetail>
        <Icon icon={XIcon} onClick={() => onRemove(label)} />
      </LabelDetail>
    )}
  </SLabel>
);

type LabelChooserProps = {
  trigger?: React.ReactElement;
  tooltip?: string;
  isOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  disabled?: boolean;
  challengeId?: string;
  existing?: string[];
  onComplete: (label: string) => void;
  onRemoveExisting: (label: string) => void;
};

export const LabelChooser = ({
  isOpen,
  onOpen,
  onClose,
  trigger,
  tooltip,
  disabled,
  challengeId,
  existing,
  onComplete,
  onRemoveExisting,
}: LabelChooserProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<string | null>(null);

  const user = useAppSelector((state) => state.user);

  const canManageChallenges = hasPermission(
    user,
    "org.manageChallenges",
    user?.organisation,
  );
  const canViewSettings = challengeId
    ? hasPermission(user, "challenge.editIdeaLabels", challengeId)
    : true;

  // Button handlers for manually opening and closing the chooser popup
  const closePopup = useCallback(() => {
    if (open && onClose) {
      onClose();
    }
    setOpen(false);
  }, [open, onClose]);

  const openPopup = useCallback(() => {
    if (open) {
      return closePopup();
    }
    if (!open && onOpen) onOpen();
    setOpen(true);
  }, [open, onOpen, closePopup]);

  const triggerElement = useMemo(() => {
    if (trigger) {
      return cloneElement(trigger, {
        onClick: disabled ? () => {} : openPopup,
      } as React.HTMLAttributes<HTMLElement>);
    }
    return null;
  }, [trigger, openPopup, disabled]);

  useEffect(() => {
    if (isOpen !== undefined && open !== isOpen) {
      setOpen(isOpen);
    }
  }, [open, isOpen]);

  const labelQuery = useQuery(
    initUseQuery(api.challenges.getLabels, [], {
      isEnabled: canManageChallenges || canViewSettings,
    }),
  );

  const chooseLabel = (label: string) => {
    if (existing?.includes(label)) {
      onRemoveExisting(label);

      api.setQueryData(api.challenges.getLabels, [], (prev) => {
        if (!prev) {
          return { labels: [label], challengeLabels: [label] };
        }
        return {
          ...prev,
          labels: (prev.labels ?? []).filter((l) => l !== label),
        };
      });
    } else {
      onComplete(label);

      api.setQueryData(
        api.challenges.getLabels,
        [],
        (prev) => {
          if (!prev) {
            return { labels: [label], challengeLabels: [label] };
          }
          return {
            ...prev,
            labels: (prev.labels ?? []).includes(label)
              ? (prev.labels ?? [])
              : [...(prev.labels ?? []), label],
          };
        },
        { allowUndefined: true },
      );
    }
  };

  return (
    <>
      <Modal open={!!open} onClose={() => closePopup()}>
        <ModalHeader>Manage labels</ModalHeader>
        <ModalContent>
          <p>
            Labels help categorise and filter {t("generic.ideas")} within{" "}
            {t("generic.challengeWithArticle")}. Labels are only visibile to
            managers with appropriate permissions.
          </p>
          {(existing?.length ?? 0) > 0 ? (
            <div style={{ marginBottom: 5 }}>
              <H4 style={{ marginBottom: 5 }}>Selected labels</H4>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {existing?.map((label) => (
                  <LabelItem
                    key={label}
                    label={label}
                    onRemove={(label) => onRemoveExisting(label)}
                    size="medium"
                  />
                ))}
              </div>
            </div>
          ) : null}
          <H4>
            Choose or add a label
            <Subheader>
              Start typing to search existing labels or add a new one by typing
              it in full and pressing enter.
            </Subheader>
          </H4>
          <Dropdown
            loading={labelQuery.isLoading}
            placeholder="Apply a label..."
            variant="outline"
            search
            allowAdditions
            onChange={(e, { value }) => {
              chooseLabel(value as string);
              setValue("");
            }}
            value={value ?? ""}
            options={
              labelQuery.data?.labels?.map((label) => ({
                icon: existing?.includes(label) ? CheckIcon : undefined,
                key: label,
                text: label,
                value: label,
              })) || []
            }
          />
        </ModalContent>
        <ModalActions>
          <Button onClick={() => closePopup()}>Close</Button>
        </ModalActions>
      </Modal>
      <Popup
        on="hover"
        trigger={triggerElement}
        disabled={!tooltip || disabled}
      >
        {tooltip}
      </Popup>
    </>
  );
};
